import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import { isValidCPF } from '../../utils/ValidarCPF';
import isValidCNPJ from '../../utils/ValidarCNPJ';
import * as z from 'zod';
import { emailSchema } from '../../utils/validations';
import PasswordInput from '../PasswordInput';

interface CadastroUsuarioProps {
  open: boolean;
  onClose: () => void;
}

export const maskCpfCnpj = (value: string): string => {
  let v = value.replace(/\D/g, '');

  if (v.length > 14) v = v.slice(0, 14);

  if (v.length <= 11) {
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d)/, '$1.$2');
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    v = v.replace(/^(\d{2})(\d)/, '$1.$2');
    v = v.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
    v = v.replace(/\.(\d{3})(\d)/, '.$1/$2');
    v = v.replace(/(\d{4})(\d)/, '$1-$2');
  }

  return v;
};

export const maskPhoneNumber = (value: string): string => {
  let v = value.replace(/\D/g, '');
  if (v.length > 11) v = v.slice(0, 11);

  if (v.length <= 10) {
    v = v.replace(/(\d{2})(\d)/, '($1) $2');
    v = v.replace(/(\d{4})(\d)/, '$1-$2');
  } else {
    v = v.replace(/(\d{2})(\d)/, '($1) $2');
    v = v.replace(/(\d{5})(\d)/, '$1-$2');
  }
  return v;
};

const CadastroUsuario: React.FC<CadastroUsuarioProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    cpfCnpj: '',
    senha: '',
    confirmarSenha: '',
    numeroTelefone: '',
  });

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: 'error' | 'success';
    msg: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'cpfCnpj') {
      setFormData({ ...formData, [name]: maskCpfCnpj(value) });
    } else if (name === 'numeroTelefone') {
      const onlyDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, [name]: maskPhoneNumber(onlyDigits) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleCadastro = async () => {
    setFeedback(null);

    // Validação genérica
    const isFormValid = Object.values(formData).every(
      value => value.trim() !== '',
    );

    if (!isFormValid) {
      setFeedback({
        type: 'error',
        msg: 'Por favor, preencha todos os campos.',
      });
      return;
    }

    // Verificação de e-mail
    try {
      emailSchema.parse(formData.email);
    } catch (err) {
      if (err instanceof z.ZodError) {
        setFeedback({ type: 'error', msg: err.errors[0].message });
        return;
      }
    }

    // Validação de CPF/CNPJ
    const digits = formData.cpfCnpj.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      setFeedback({
        type: 'error',
        msg: 'Documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos.',
      });
      return;
    }
    const isCpf = digits.length === 11;
    const isValid = isCpf ? isValidCPF(digits) : isValidCNPJ(digits);
    if (!isValid) {
      setFeedback({
        type: 'error',
        msg: `${isCpf ? 'CPF' : 'CNPJ'} inválido.`,
      });
      return;
    }

    const telefoneRegex = /^\(\d{2}\) \d{5}-\d{4}$/;
    if (!telefoneRegex.test(formData.numeroTelefone)) {
      setFeedback({
        type: 'error',
        msg: 'Telefone inválido. O formato correto é (00) 00000-0000.',
      });
      return;
    }

    if (formData.senha !== formData.confirmarSenha) {
      setFeedback({
        type: 'error',
        msg: 'As senhas não coincidem.',
      });
      return;
    }

    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_OG_API_URL}/usuario-externo/cadastro`;

      const payload = {
        name: formData.nome,
        email: formData.email,
        cpfCnpj: formData.cpfCnpj.replace(/\D/g, ''),
        ddd: formData.numeroTelefone.replace(/\D/g, '').slice(0, 2),
        numero: formData.numeroTelefone.replace(/\D/g, '').slice(2),
        password: formData.senha,
      };

      await axios.post(url, payload);

      setFeedback({
        type: 'success',
        msg: 'Cadastro realizado com sucesso! Faça login.',
      });

      setTimeout(() => {
        onClose();
        setFeedback(null);
        setFormData({
          nome: '',
          email: '',
          cpfCnpj: '',
          senha: '',
          confirmarSenha: '',
          numeroTelefone: '',
        });
      }, 1000);
    } catch (error: any) {
      setFeedback({
        type: 'error',
        msg: error.response?.data?.message || 'Erro no cadastro.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        Cadastro de Usuário
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Preencha os dados abaixo para criar sua conta.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {feedback && (
          <Alert severity={feedback.type} sx={{ mb: 3 }}>
            {feedback.msg}
          </Alert>
        )}

        <Box
          display="grid"
          gap={3}
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        >
          {/* Nome Completo (Ocupa a linha toda) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              autoFocus
              name="nome"
              label="Nome Completo"
              fullWidth
              variant="outlined"
              value={formData.nome}
              onChange={handleChange}
              required
            />
          </Box>

          {/* E-mail (Ocupa a linha toda) */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              name="email"
              label="E-mail"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Box>

          {/* CPF/CNPJ (Ocupa 1 coluna em telas grandes) */}
          <Box>
            <TextField
              name="cpfCnpj"
              label="CPF ou CNPJ"
              fullWidth
              variant="outlined"
              value={formData.cpfCnpj}
              onChange={handleChange}
              required
              inputProps={{ maxLength: 18 }}
            />
          </Box>

          {/* Telefone (Ocupa 1 coluna em telas grandes) */}
          <Box>
            <TextField
              name="numeroTelefone"
              label="Número de Telefone"
              fullWidth
              variant="outlined"
              value={formData.numeroTelefone}
              onChange={handleChange}
              required
              inputProps={{ maxLength: 15 }}
            />
          </Box>

          {/* Senha */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <PasswordInput
              fullWidth
              name="senha"
              value={formData.senha}
              error={false}
              onChange={handleChange}
              required
            />
          </Box>

          {/* Confirmar Senha */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <PasswordInput
              fullWidth
              name="confirmarSenha"
              value={formData.confirmarSenha}
              error={false}
              onChange={handleChange}
              label="Confirmar Senha"
              required
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleCadastro}
          variant="contained"
          color="primary"
          disabled={loading}
          disableElevation
          sx={{ textTransform: 'none', px: 4 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Cadastrar'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CadastroUsuario;
